import React, { useEffect, useRef, useState } from 'react';

import { Copy, MoreVertical, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';

import { IdentityBadge } from '@/components/badges/IdentityBadge';
import { OperationalBadge } from '@/components/badges/OperationalBadge';
import { StateBadge } from '@/components/badges/StateBadge';
import apiClient from '../../services/apiClient';
import { ProjectEditModal } from '@/components/projects/ProjectEditModal';

export type ProjectCardModel = {
  id: string;
  title: string;
  description?: string;
  updatedLabel?: string;
  isCreator?: boolean;
  workstreamCount: number;
  taskCount: number;
  actionItemCount: number;
  progressPct?: number | null;
  state?: string | null;
  sealedAt?: string | null;
  drifted?: boolean;
  stalledSeverity?: string | null;
};

function clampPct(v: any): number {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, Math.round(n)));
}

export function ProjectCard({ project, onOpen }: { project: ProjectCardModel; onOpen: () => void }) {
  const progress = clampPct(project.progressPct);
  const state = String(project.state || 'inferred').toLowerCase();
  const showSealed = !!project.sealedAt;
  const showCreator = project.isCreator !== false;
  const stalledSeverity = String(project.stalledSeverity || '').toLowerCase();
  const stalledSev =
    stalledSeverity === 'high' || stalledSeverity === 'critical'
      ? 'high'
      : stalledSeverity === 'med' || stalledSeverity === 'medium'
        ? 'medium'
        : stalledSeverity === 'low'
          ? 'low'
          : undefined;

  // Ideas Kitchen-ish deterministic color system (readable in light + dark).
  const cardTone =
    showSealed
      ? {
          topLine: 'bg-gradient-to-r from-white via-fuchsia-400 to-white',
          border: 'border-fuchsia-100 dark:border-fuchsia-900/30',
          glow: 'shadow-[0_18px_55px_-35px_rgba(236,72,153,0.28)] hover:shadow-[0_24px_70px_-35px_rgba(236,72,153,0.36)]',
          hoverTitle: 'group-hover:text-fuchsia-600 dark:group-hover:text-fuchsia-300',
        }
      : state === 'committed'
        ? {
            topLine: 'bg-gradient-to-r from-white via-emerald-400 to-white',
            border: 'border-emerald-100 dark:border-emerald-900/30',
            glow: 'shadow-[0_18px_55px_-35px_rgba(16,185,129,0.24)] hover:shadow-[0_24px_70px_-35px_rgba(16,185,129,0.32)]',
            hoverTitle: 'group-hover:text-emerald-700 dark:group-hover:text-emerald-300',
          }
        : state === 'delegated'
          ? {
              topLine: 'bg-gradient-to-r from-white via-amber-400 to-white',
              border: 'border-amber-100 dark:border-amber-900/30',
              glow: 'shadow-[0_18px_55px_-35px_rgba(245,158,11,0.24)] hover:shadow-[0_24px_70px_-35px_rgba(245,158,11,0.32)]',
              hoverTitle: 'group-hover:text-amber-700 dark:group-hover:text-amber-300',
            }
          : project.drifted
            ? {
                topLine: 'bg-gradient-to-r from-white via-orange-400 to-white',
                border: 'border-orange-100 dark:border-orange-900/30',
                glow: 'shadow-[0_18px_55px_-35px_rgba(249,115,22,0.24)] hover:shadow-[0_24px_70px_-35px_rgba(249,115,22,0.32)]',
                hoverTitle: 'group-hover:text-orange-700 dark:group-hover:text-orange-300',
              }
            : project.stalledSeverity
              ? {
                  topLine: 'bg-gradient-to-r from-white via-rose-400 to-white',
                  border: 'border-rose-100 dark:border-rose-900/30',
                  glow: 'shadow-[0_18px_55px_-35px_rgba(244,63,94,0.24)] hover:shadow-[0_24px_70px_-35px_rgba(244,63,94,0.32)]',
                  hoverTitle: 'group-hover:text-rose-700 dark:group-hover:text-rose-300',
                }
              : {
                  topLine: 'bg-gradient-to-r from-white via-sky-400 to-white',
                  border: 'border-sky-100 dark:border-sky-900/30',
                  glow: 'shadow-[0_18px_55px_-38px_rgba(59,130,246,0.20)] hover:shadow-[0_24px_70px_-38px_rgba(59,130,246,0.26)]',
                  hoverTitle: 'group-hover:text-sky-700 dark:group-hover:text-sky-300',
                };

  const [showMenu, setShowMenu] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!showMenu) return;
    const onDoc = (e: MouseEvent) => {
      try {
        const el = menuRef.current;
        if (!el) return;
        if (el.contains(e.target as any)) return;
        setShowMenu(false);
      } catch {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [showMenu]);

  async function doDelete() {
    try {
      await apiClient.delete(`/projects/${encodeURIComponent(project.id)}`);
      toast.success('Project deleted');
      try {
        window.dispatchEvent(new CustomEvent('project.deleted'));
        window.dispatchEvent(new CustomEvent('project.tasks.refresh'));
      } catch {}
    } catch (e: any) {
      toast.error(e?.message || 'Failed to delete project');
    }
  }

  async function doCopy() {
    try {
      await apiClient.post(`/projects/${encodeURIComponent(project.id)}/copy`, {});
      toast.success('Project copied');
      try {
        window.dispatchEvent(new CustomEvent('project.created'));
        window.dispatchEvent(new CustomEvent('project.tasks.refresh'));
      } catch {}
    } catch (e: any) {
      toast.error(e?.message || 'Failed to copy project');
    }
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onOpen();
      }}
      className={`group relative w-full rounded-2xl border bg-white dark:bg-neutral-900 ${cardTone.border} ${cardTone.glow} transition-all duration-300 hover:-translate-y-1 overflow-hidden p-5 text-left`}
    >
      {/* Top gradient line (Ideas Kitchen signal) */}
      <div className={`absolute left-0 right-0 top-0 h-1 ${cardTone.topLine} opacity-60 transition-opacity duration-500 group-hover:opacity-100`} />

      {/* Soft glow blobs */}
      <div className="pointer-events-none absolute inset-0 opacity-70">
        <div className="absolute -left-24 -top-24 h-56 w-56 rounded-full bg-fuchsia-500/8 blur-2xl" />
        <div className="absolute -bottom-24 -right-24 h-56 w-56 rounded-full bg-sky-500/8 blur-2xl" />
      </div>

      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className={`text-lg font-bold tracking-tight text-slate-900 dark:text-white ${cardTone.hoverTitle} line-clamp-2`}>
            {project.title}
          </div>
          <div className="mt-1 text-[11px] font-extrabold uppercase tracking-wide text-slate-400 dark:text-neutral-500">
            Mission
          </div>
          {project.description ? (
            <div className="mt-1 line-clamp-3 text-sm font-medium text-slate-600 dark:text-neutral-200">
              {project.description}
            </div>
          ) : (
            <div className="mt-1 text-sm font-medium text-slate-500 dark:text-neutral-300">No mission yet.</div>
          )}
        </div>
        <div className="flex flex-col items-end gap-2">
          {/* Three-dots menu */}
          <div className="relative" ref={menuRef}>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowMenu((v) => !v);
              }}
              className="rounded-lg p-1 hover:bg-slate-100 dark:hover:bg-neutral-900"
              aria-label="Project menu"
              title="Project menu"
            >
              <MoreVertical className="h-4 w-4 text-slate-500 dark:text-neutral-400" />
            </button>
            {showMenu ? (
              <div className="absolute right-0 top-full z-50 mt-2 w-fit whitespace-nowrap rounded-xl border border-slate-200 bg-white py-1 shadow-lg dark:border-neutral-800 dark:bg-neutral-950">
                <button
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-slate-700 hover:bg-slate-50 dark:text-neutral-200 dark:hover:bg-neutral-900"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowMenu(false);
                    setShowEdit(true);
                  }}
                >
                  <Pencil className="h-3.5 w-3.5" />
                  Edit
                </button>
                <button
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-slate-700 hover:bg-slate-50 dark:text-neutral-200 dark:hover:bg-neutral-900"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowMenu(false);
                    void doCopy();
                  }}
                >
                  <Copy className="h-3.5 w-3.5" />
                  Copy Project
                </button>
                <div className="my-1 h-px bg-slate-200 dark:bg-neutral-800" />
                <button
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-rose-700 hover:bg-rose-50 dark:text-rose-300 dark:hover:bg-rose-900/20"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowMenu(false);
                    void doDelete();
                  }}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Delete
                </button>
              </div>
            ) : null}
          </div>
          {/* Identity badges stay up here (governance) */}
          {showCreator ? <IdentityBadge type="creator" /> : null}
        </div>
      </div>

      <div className="relative mt-4 grid grid-cols-3 gap-2">
        <div className="rounded-xl border border-slate-100 bg-slate-50/70 px-3 py-2 dark:border-neutral-800 dark:bg-neutral-950/50">
          <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500 dark:text-neutral-400">
            Workstreams
          </div>
          <div className="mt-1 text-lg font-extrabold text-slate-900 dark:text-white">{project.workstreamCount}</div>
        </div>
        <div className="rounded-xl border border-slate-100 bg-slate-50/70 px-3 py-2 dark:border-neutral-800 dark:bg-neutral-950/50">
          <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500 dark:text-neutral-400">Tasks</div>
          <div className="mt-1 text-lg font-extrabold text-slate-900 dark:text-white">{project.taskCount}</div>
        </div>
        <div className="rounded-xl border border-slate-100 bg-slate-50/70 px-3 py-2 dark:border-neutral-800 dark:bg-neutral-950/50">
          <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500 dark:text-neutral-400">
            Action Items
          </div>
          <div className="mt-1 text-lg font-extrabold text-slate-900 dark:text-white">{project.actionItemCount}</div>
        </div>
      </div>

      {/* Bottom strip: left = state/health (intelligence), right = progress & updated */}
      <div className="relative mt-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <StateBadge state={showSealed ? 'sealed' : (state as any)} />
          {project.drifted ? <OperationalBadge type="drifted" /> : null}
          {project.stalledSeverity ? <OperationalBadge type="stalled" severity={stalledSev as any} /> : null}
        </div>

        <div className="flex items-center gap-2">
          {project.updatedLabel ? (
            <span className="rounded-full border border-slate-200 bg-white/75 px-2 py-0.5 text-[10px] font-bold text-slate-700 dark:border-neutral-800 dark:bg-neutral-950/60 dark:text-neutral-200">
              updated {project.updatedLabel}
            </span>
          ) : null}
          <span className="rounded-full border border-slate-200 bg-white/75 px-2 py-0.5 text-[10px] font-bold text-slate-800 dark:border-neutral-800 dark:bg-neutral-950/60 dark:text-neutral-100">
            Progress: {progress}%
          </span>
        </div>
      </div>

      <ProjectEditModal
        open={showEdit}
        projectId={project.id}
        initialTitle={project.title}
        initialObjective={project.description || ''}
        onClose={() => setShowEdit(false)}
        onSaved={() => {
          try {
            window.dispatchEvent(new CustomEvent('project.tasks.refresh'));
          } catch {}
        }}
      />
    </div>
  );
}

